import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';
import { FRASE_DA_MARCA } from '../utils/marca';
import GraficoDoLogo from './GraficoDoLogo';
import EntradaComSenha from './EntradaComSenha';
import { LOGIN_COM_SENHA } from '../utils/loginComSenha';

const LoginPage: React.FC = () => {
  const { signInWithGoogle } = useAuth();

  return (
    <div className="login-page">
      {/*
        O gráfico de fundo — decoração, e só.

        É o gráfico do LOGO (barras, onda e ponto), o mesmo da tela de carga,
        desde 05/10: as duas telas aparecem em sequência para quem entra, e
        tinham fundos diferentes. Mora em `GraficoDoLogo.tsx` — um componente
        só, para não existirem duas cópias de um elemento de marca.

        A variante `login` é a de rodapé: baixa, no espaço embaixo do cartão.
      */}
      <GraficoDoLogo variante="login" />

      <div className="login-card">
        {/*
          `.login-brand` VOLTOU, e desta vez com o ícone dentro — empilhado.

          Ele já existiu aqui como um flex para alinhar um ícone ao lado do
          nome, e saiu em 27/08 porque o ícone nunca tinha entrado no JSX — um
          flex de um filho só era uma div a mais. Agora a marca existe, e o
          lugar dela é este: a tela de login é onde alguém vê o produto pela
          primeira vez, e é onde a marca inteira — símbolo, nome e o que ele
          faz — precisa aparecer junta.

          No cabeçalho do app ela não fica: lá dentro a pessoa já sabe onde
          está, e o cabeçalho volta a ser o nome e a frase, como era.

          Ícone em cima, nome, frase — nessa ordem, centralizados. Uma versão
          com o ícone à esquerda foi tentada; ela preferiu esta, que também
          conserta o cartão saindo da tela em aparelho estreito (ver o CSS).

          O ícone é o `favicon.svg` por `<img>`, e não embutido: existe um
          arquivo só desenhando esta marca, e embutir aqui criaria mais uma
          cópia da geometria.

          `aria-hidden` porque o `<h1>` ao lado já diz "FinTracking" — para
          quem usa leitor de tela, anunciar o ícone repetiria o nome.
        */}
        <div className="login-brand">
          <img className="login-brand-icone" src={`${process.env.PUBLIC_URL}/icons/favicon.svg`}
               alt="" aria-hidden="true" width={64} height={64} />
          <div className="login-brand-texto">
            <h1 className="login-title">
              <span className="logo-text-login">
                <span className="logo-fin-login">Fin</span>
                <span className="logo-track-login">Tracking</span>
              </span>
            </h1>
            {/* A mesma frase do cabeçalho do app, do mesmo lugar. Ela já mudou
                uma vez, e naquela hora estava em cinco arquivos. */}
            <p className="login-subtitle">{FRASE_DA_MARCA}</p>
          </div>
        </div>

        <button className="login-google-btn" onClick={signInWithGoogle}>
          <svg className="login-google-icon" viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Entrar com Google
        </button>

        {/*
          A outra forma de entrar, fechada até ela pedir — ver o componente.

          Atrás de uma CHAVE (`REACT_APP_LOGIN_COM_SENHA=1`) porque o código
          sobe antes da configuração: enquanto o provedor de email não estiver
          ligado no painel do Supabase, com confirmação obrigatória e o envio
          pelo Resend, o botão levaria a um erro genérico — ou, pior, a um
          cadastro sem confirmação. Liga-se a chave na Vercel depois de o
          painel estar pronto e testado. Ver `docs/designs/login-com-senha.md`.
        */}
        {LOGIN_COM_SENHA && (
          <>
            <div className="login-ou" aria-hidden="true"><span>ou</span></div>
            <EntradaComSenha />
          </>
        )}

        {/*
          A promessa, e ao lado dela onde conferi-la.

          "Seus dados ficam seguros e acessíveis apenas por você" é uma
          afirmação forte para ficar sozinha — sem um lugar que a detalhe, ela
          é marketing. O link é o que a torna verificável.

          E o Google pede a política acessível sem login para a
          brand-verification; este é o caminho mais óbvio até ela.
        */}
        <p className="login-disclaimer">
          Seus dados ficam seguros e acessíveis apenas por você.{' '}
          <a className="login-privacidade" href="/privacidade">
            Política de privacidade
          </a>
        </p>

        {/*
          O gancho para a apresentação (05/10).

          Quem digita o endereço, ou chega por um link antigo, cai aqui: um
          cartão com o nome, uma frase e o botão de entrar. Para quem não
          conhece o app, isso não explica nada — e a página que explica existia
          sem caminho nenhum até ela a partir daqui.

          Fica por último e discreto: quem já tem conta não deve tropeçar nele
          a caminho do botão.
        */}
        <a className="login-conheca" href="/conheca">
          Ainda não conhece? Veja como funciona <span aria-hidden="true">→</span>
        </a>
        {/* E para quem quer ver por dentro antes de criar a conta: o app de
            verdade com os dados de uma pessoa inventada. Ver `demo/modo.ts`. */}
        <a className="login-demo" href="/demo">Experimentar sem cadastro</a>
      </div>
    </div>
  );
};

export default LoginPage;
